/**
 * Escape a value for CSV, handling special characters and CSV injection
 */
function escapeCSVValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  let str = String(value);
  // Prevent CSV injection
  if (/^[=+\-@\t\r]/.test(str)) str = "'" + str;
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

interface ExportColumn<T> {
  label: string;
  noFilter?: boolean;
  getValue: (item: T) => string | number | null;
}

/**
 * Export items to CSV file and trigger download
 */
export function exportToCSV<T>(
  items: T[],
  columns: ExportColumn<T>[],
  filename: string,
): void {
  const exportCols = columns.filter((c) => !c.noFilter);
  const header = exportCols.map((c) => escapeCSVValue(c.label)).join(',');
  const rows = items.map((item) =>
    exportCols.map((c) => escapeCSVValue(c.getValue(item))).join(',')
  );
  const csvContent = [header, ...rows].join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Copy items as tab-separated text to clipboard
 */
export function copyToClipboard<T>(
  items: T[],
  columns: ExportColumn<T>[],
): Promise<void> {
  const exportCols = columns.filter((c) => !c.noFilter);
  const header = exportCols.map((c) => c.label).join('\t');
  const rows = items.map((item) =>
    exportCols.map((c) => String(c.getValue(item) ?? '')).join('\t')
  );
  const text = [header, ...rows].join('\n');
  return navigator.clipboard.writeText(text);
}
